import React, { useEffect, useState, createContext, useContext, useMemo } from 'react';
import { Snackbar, Alert, Box, Typography, Chip, Paper } from '@mui/material';
import { swarmApi } from '../api';

const LiveEventsContext = createContext({
  events: [],
  toast: null,
  setToast: () => {},
  connected: false,
});

export function useLiveEvents() {
  return useContext(LiveEventsContext);
}

export function LiveEventsProvider({ enabled, children }) {
  const [events, setEvents] = useState([]);
  const [toast, setToast] = useState(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    let es;
    let closed = false;

    es = swarmApi.openEventStream(
      (type, data) => {
        if (type === 'connected') {
          setConnected(true);
          return;
        }
        const entry = {
          id: `${Date.now()}-${Math.random()}`,
          type: data?.type || type,
          message: data?.message || String(data),
          resource: data?.resource,
          timestamp: data?.timestamp || new Date().toISOString(),
        };
        setEvents((prev) => [entry, ...prev].slice(0, 40));
        if (['service_state', 'ai_recommendation', 'ai_decision', 'autoscale', 'intent'].includes(entry.type)) {
          setToast(entry);
        }
      },
      () => setConnected(false)
    );

    return () => {
      closed = true;
      es?.close();
    };
  }, [enabled]);

  const value = useMemo(
    () => ({ events, toast, setToast, connected }),
    [events, toast, connected]
  );

  return (
    <LiveEventsContext.Provider value={value}>
      {children}
      <LiveEventToasts toast={toast} onClose={() => setToast(null)} />
    </LiveEventsContext.Provider>
  );
}

export function LiveEventToasts({ toast, onClose }) {
  if (!toast) return null;
  const severity =
    toast.type === 'ai_recommendation' || toast.type === 'service_state' ? 'warning'
      : toast.type === 'autoscale' || toast.type === 'ai_decision' ? 'info'
        : 'info';
  return (
    <Snackbar
      open={!!toast}
      autoHideDuration={6000}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    >
      <Alert onClose={onClose} severity={severity} variant="filled" sx={{ maxWidth: 420 }}>
        <Typography variant="caption" display="block" sx={{ opacity: 0.85 }}>{toast.type}</Typography>
        {toast.message}
      </Alert>
    </Snackbar>
  );
}

export function LiveEventPanel() {
  const { events, connected } = useLiveEvents();
  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>Live activity</Typography>
        <Chip size="small" label={connected ? 'SSE live' : 'SSE offline'} color={connected ? 'success' : 'default'} />
      </Box>
      {events.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Waiting for service state changes, AI actions, and autoscale events…
        </Typography>
      ) : (
        <Box sx={{ maxHeight: 280, overflow: 'auto' }}>
          {events.map((e) => (
            <Box key={e.id} sx={{ py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Chip size="small" label={e.type} variant="outlined" />
                <Typography variant="caption" color="text.secondary">
                  {e.timestamp ? new Date(e.timestamp).toLocaleTimeString() : ''}
                </Typography>
              </Box>
              <Typography variant="body2">{e.message}</Typography>
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  );
}

/** @deprecated use LiveEventsProvider */
export default function LiveEventsHost({ auth }) {
  return <LiveEventsProvider enabled={!!auth}>{null}</LiveEventsProvider>;
}
